import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client.js';
import CustomerSearchSelect from '../components/CustomerSearchSelect.jsx';

const CATEGORY_LABEL = {
  customer: '🏢 Customer', contact: '👤 Contact', product: '🚚 Product',
  logistics: '📦 Logistics', other: '💬 Other',
};

export default function AIQuestions() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['ai-questions', 'pending'],
    queryFn: () => api.get('/ai-questions?status=pending').then(r => r.data.data),
  });

  return (
    <div className="space-y-4 max-w-2xl">
      <header>
        <div className="section-eyebrow">Inbox</div>
        <h1 className="text-2xl">AI Questions</h1>
        <div className="rule-crimson" />
        <p className="text-sm text-muted">Quick clarifications about your recent notes.</p>
      </header>

      <div className="space-y-3">
        {list.data?.length === 0 && (
          <div className="card p-4 text-muted text-sm">All caught up.</div>
        )}
        {list.data?.map(q => <QuestionCard key={q.id} q={q} qc={qc} />)}
      </div>
    </div>
  );
}

function QuestionCard({ q, qc }) {
  const [answer, setAnswer] = useState('');
  const [customerId, setCustomerId] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [customerNameHint, setCustomerNameHint] = useState('');
  // Offer the customer lookup whenever this note isn't linked to a customer yet.
  const canLinkCustomer = !q.customer_id;

  const submit = useMutation({
    mutationFn: () => api.post(`/ai-questions/${q.id}/answer`, {
      answer,
      ...(customerId ? { customer_id: customerId } : {}),
      ...(customerNameHint ? { customer_name_hint: customerNameHint } : {}),
    }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-questions'] }),
  });
  const dismiss = useMutation({
    mutationFn: () => api.post(`/ai-questions/${q.id}/dismiss`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-questions'] }),
  });

  const canSubmit = (answer.trim() || customerId || customerNameHint) && !submit.isPending;

  return (
    <div className="card p-4 border-l-4 border-crimson">
      <div className="flex items-start justify-between gap-2">
        <div className="font-medium text-navy">{q.question_text}</div>
        {q.category && (
          <span className="text-[10px] uppercase tracking-wider text-muted bg-panel rounded px-1.5 py-0.5 whitespace-nowrap">
            {CATEGORY_LABEL[q.category] || q.category}
          </span>
        )}
      </div>
      {q.context && (
        <div className="text-xs text-muted mt-1 italic">"{q.context}"</div>
      )}
      {q.customer_name && (
        <div className="text-xs text-muted mt-1">On note for <span className="text-navy">{q.customer_name}</span></div>
      )}

      {canLinkCustomer && (
        <div className="mt-3">
          <label className="label text-xs">Link the customer this note is about</label>
          <CustomerSearchSelect
            value={customerId} valueLabel={customerName} hintValue={customerNameHint}
            onChange={(id, name, isHint) => {
              if (isHint) { setCustomerId(null); setCustomerName(''); setCustomerNameHint(name); }
              else { setCustomerId(id); setCustomerName(name || ''); setCustomerNameHint(''); }
            }}
          />
        </div>
      )}

      <textarea
        className="input mt-3 min-h-[80px]"
        placeholder={canLinkCustomer ? 'Add a note (optional if you linked a customer)…' : 'Your answer…'}
        value={answer}
        onChange={e => setAnswer(e.target.value)}
      />
      <div className="flex justify-end gap-2 mt-2">
        <button className="btn-ghost text-sm" onClick={() => dismiss.mutate()} disabled={dismiss.isPending}>Dismiss</button>
        <button className="btn-primary text-sm" disabled={!canSubmit} onClick={() => submit.mutate()}>
          {submit.isPending ? 'Sending…' : (customerId || customerNameHint) && !answer.trim() ? 'Link customer' : 'Answer'}
        </button>
      </div>
      {submit.isError && (
        <div className="text-crimson text-xs mt-1">{submit.error?.response?.data?.error || 'Failed to submit'}</div>
      )}
      <div className="text-[10px] text-muted mt-1">
        Asked {new Date(q.asked_at).toLocaleString()} · escalation tier {q.escalation_level}
      </div>
    </div>
  );
}
